import React, { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useUserAnalytics, useSessionAnalytics, useCrashAnalytics } from '../../../hooks/useAnalytics';
import { useNormalizedUserData } from '../../../hooks/useNormalizedData';
import MetricCard from '../core/MetricCard';
import AreaChart from '../../ui/AreaChart';
import PieChart from '../../ui/PieChart';
import CollapsibleSection from '../../ui/CollapsibleSection';
import { transformToChartData, transformToChartDataAsync } from '../../../services/chartService';
import { getPreviousPeriodValue } from '../../../utils/chartHelpers';
import { formatPercent, formatNumberCompact, safeFormatFixed } from '../../../utils/format';
import { FILTER_PERIODS } from '../../../constants';

/**
 * Metrics Overview Component
 * Displays key user, session, and crash metrics in a grid layout
 * @param {Object} props - Component props
 * @param {string} props.period - Time period for metrics
 * @param {string|null} [props.platform] - Platform filter
 * @param {boolean} [props.compact] - Whether to display in compact mode
 */
const MetricsOverview = ({ period, platform, compact = false }) => {
  const { data: userData, loading: userLoading, error: userError } = useUserAnalytics(period, platform);
  const { data: sessionData, loading: sessionLoading, error: sessionError } = useSessionAnalytics(period);
  const { data: crashData, loading: crashLoading, error: crashError } = useCrashAnalytics(period);

  // Normalize data for safe access
  const normalized = useNormalizedUserData(userData, sessionData, crashData);

  // Generate chart data for key metrics using real data
  const [newUsersChart, setNewUsersChart] = useState(null);
  const [sessionsChart, setSessionsChart] = useState(null);
  const [previousUserData, setPreviousUserData] = useState(null);
  const [previousSessionData, setPreviousSessionData] = useState(null);

  // Fetch previous period data for accurate comparison
  useEffect(() => {
    const fetchPreviousPeriodData = async () => {
      try {
        // Get previous period mapping
        const prevPeriodMap = {
          [FILTER_PERIODS.DAY]: FILTER_PERIODS.DAY,
          [FILTER_PERIODS.WEEK]: FILTER_PERIODS.WEEK,
          [FILTER_PERIODS.MONTH]: FILTER_PERIODS.MONTH,
          [FILTER_PERIODS.YEAR]: FILTER_PERIODS.YEAR,
          [FILTER_PERIODS.ALL]: FILTER_PERIODS.YEAR
        };
        
        const prevPeriod = prevPeriodMap[period] || FILTER_PERIODS.DAY;
        
        // Fetch previous period data in parallel
        const { getUserAnalytics, getSessionAnalytics } = await import('../../../services/firebaseService');
        const [prevUser, prevSession] = await Promise.all([
          getUserAnalytics(prevPeriod, platform).catch(() => null),
          getSessionAnalytics(prevPeriod).catch(() => null)
        ]);
        
        setPreviousUserData(prevUser);
        setPreviousSessionData(prevSession);
      } catch (error) {
        // If fetching previous period fails, components will use calculated estimates
        setPreviousUserData(null);
        setPreviousSessionData(null);
      }
    };

    if (userData && sessionData) {
      fetchPreviousPeriodData();
    }
  }, [period, platform, userData, sessionData]);

  useEffect(() => {
    if (!userData || !sessionData) {
      setNewUsersChart(null);
      setSessionsChart(null);
      return;
    }

    // Use actual previous period data if available, otherwise calculate from growth or use estimate
    const currentUsers = userData.periodUserCount || 0;
    const previousUsers = getPreviousPeriodValue(userData, previousUserData, 'periodUserCount', 0.9);

    const currentSessions = sessionData.totalSessions || 0;
    const previousSessions = getPreviousPeriodValue(sessionData, previousSessionData, 'totalSessions', 0.9);

    // Try to fetch real data from users collection
    Promise.all([
      transformToChartDataAsync(
        { periodUserCount: currentUsers },
        { periodUserCount: previousUsers },
        'periodUserCount',
        period,
        {
          collectionName: 'users',
          valueExtractor: () => 1 // Count users
        }
      ),
      transformToChartDataAsync(
        { totalSessions: currentSessions },
        { totalSessions: previousSessions },
        'totalSessions',
        period,
        {
          collectionName: 'users',
          valueExtractor: () => 1 // Count sessions (users who logged in)
        }
      )
    ]).then(([usersChart, sessionsChartData]) => {
      setNewUsersChart(usersChart);
      setSessionsChart(sessionsChartData);
    }).catch(() => {
      // Fallback to synchronous generated data
      setNewUsersChart(transformToChartData(
        { periodUserCount: currentUsers },
        { periodUserCount: previousUsers },
        'periodUserCount',
        period
      ));
      setSessionsChart(transformToChartData(
        { totalSessions: currentSessions },
        { totalSessions: previousSessions },
        'totalSessions',
        period
      ));
    });
  }, [userData, sessionData, previousUserData, previousSessionData, period]);

  // Memoize loading and error states
  const isLoading = useMemo(() => userLoading || sessionLoading || crashLoading, [userLoading, sessionLoading, crashLoading]);
  const hasError = useMemo(() => userError || sessionError || crashError, [userError, sessionError, crashError]);

  // Memoize platform distribution chart data
  const platformChartData = useMemo(() => {
    if (!normalized.iosUsers && !normalized.androidUsers && !normalized.solanaUsers) return null;
    return [
      { label: 'iOS', value: normalized.iosUsers },
      { label: 'Android', value: normalized.androidUsers },
      { label: 'Solana', value: normalized.solanaUsers }
    ].filter(item => item.value > 0);
  }, [normalized.iosUsers, normalized.androidUsers, normalized.solanaUsers]);

  // Error state
  if (hasError) {
    return (
      <div className="metrics-overview error">
        <h3>Error loading metrics</h3>
        <p>Please try refreshing the page</p>
      </div>
    );
  }

  return (
    <div className="metrics-overview">
      <h2 className="section-title"><i className="bi bi-people icon-brand" style={{ marginRight: 8 }}></i> User Metrics</h2>
      
      {/* Key Metrics - Only Specified Ones Visible */}
      <div className="metrics-grid-wrapper">
        <div className="metrics-grid">
          <div className="metric-group">
            <h3 className="group-title">User Metrics</h3>
            <div className="metric-cards">
            <MetricCard
              title="Total Users"
              value={normalized.totalUsers}
              formatType="number"
              loading={userLoading}
              icon={<i className="bi bi-people icon-brand"></i>}
              color="blue"
              subtitle="all time"
            />
            <MetricCard
              title="iOS Users"
              value={normalized.iosUsers}
              formatType="number"
              loading={userLoading}
              icon={<i className="bi bi-phone icon-brand"></i>}
              color="indigo"
              subtitle="iOS platform"
            />
            <MetricCard
              title="Android Users"
              value={normalized.androidUsers}
              formatType="number"
              loading={userLoading}
              icon={<i className="bi bi-robot icon-brand"></i>}
              color="green"
              subtitle="Android platform"
            />
            <MetricCard
              title="Avg Sessions/User"
              value={safeFormatFixed(normalized.averageSessionsPerUser, 2)}
              loading={sessionLoading || userLoading}
              icon={<i className="bi bi-bar-chart icon-brand"></i>}
              color="purple"
              subtitle="per user"
            />
            <MetricCard
              title="Total Crashes"
              value={normalized.totalCrashes}
              formatType="number"
              loading={crashLoading}
              icon={<i className="bi bi-exclamation-triangle icon-brand"></i>}
              color="red"
              subtitle={`${period} period`}
            />
          </div>
        </div>
        </div>
      </div>

      {/* New Users by Date Chart - Compact */}
      {newUsersChart && (
        <div className={compact ? "chart-container-compact" : "chart-container"}>
          <AreaChart
            data={newUsersChart.data}
            comparisonData={newUsersChart.comparisonData}
            label="New Users by Date"
            formatType="number"
            color="green"
          />
        </div>
      )}

      {/* All Other Metrics in Show More */}
      <CollapsibleSection
        title="Additional User Metrics"
        initialVisible={2}
        defaultExpanded={false}
      >
        {[
          <div key="additional-users" className="metric-group">
            <h3 className="group-title">Additional User Details</h3>
            <div className="metric-cards">
              <MetricCard
                title="New Users"
                value={normalized.periodUsers}
                formatType="number"
                loading={userLoading}
                icon={<i className="bi bi-person-plus icon-brand"></i>}
                color="green"
                subtitle={`${period} period`}
              />
              <MetricCard
                title="Verified Users"
                value={normalized.verifiedUsers}
                formatType="number"
                loading={userLoading}
                icon={<i className="bi bi-patch-check icon-brand"></i>}
                color="purple"
                subtitle={formatPercent(normalized.verifiedPercentage, 1)}
              />
              <MetricCard
                title="Onboarded Users"
                value={normalized.onboardedUsers}
                formatType="number"
                loading={userLoading}
                icon={<i className="bi bi-rocket-takeoff icon-brand"></i>}
                color="teal"
                subtitle={formatPercent(normalized.onboardedPercentage, 1)}
              />
              <MetricCard
                title="Wallet Users"
                value={normalized.walletUsers}
                formatType="number"
                loading={userLoading}
                icon={<i className="bi bi-wallet2 icon-brand"></i>}
                color="orange"
                subtitle={formatPercent(normalized.walletPercentage, 1)}
              />
              <MetricCard
                title="Healthy Wallets"
                value={normalized.healthyWallets}
                formatType="number"
                loading={userLoading}
                icon={<i className="bi bi-heart-fill icon-brand"></i>}
                color="green"
                subtitle={formatPercent(normalized.healthyWalletPercentage, 1)}
              />
            </div>
          </div>,

          <div key="sessions" className="metric-group">
            <h3 className="group-title">Session Metrics</h3>
            <div className="metric-cards">
              <MetricCard
                title="Total Sessions"
                value={normalized.totalSessions}
                formatType="number"
                loading={sessionLoading}
                icon={<i className="bi bi-arrow-repeat icon-brand"></i>}
                color="purple"
                subtitle={`${period} period`}
              />
              <MetricCard
                title="Active Users"
                value={normalized.activeUsers}
                formatType="number"
                loading={sessionLoading}
                icon={<i className="bi bi-lightning icon-brand"></i>}
                color="indigo"
                subtitle="logged in this period"
              />
              <MetricCard
                title="Recent Logins"
                value={normalized.recentLogins}
                formatType="number"
                loading={sessionLoading}
                icon={<i className="bi bi-key icon-brand"></i>}
                color="blue"
                subtitle="login events"
              />
            </div>
          </div>,

          <div key="crashes" className="metric-group">
            <h3 className="group-title">Crash Details</h3>
            <div className="metric-cards">
              <MetricCard
                title="iOS Crashes"
                value={normalized.iosCrashes}
                formatType="number"
                loading={crashLoading}
                icon={<i className="bi bi-phone icon-brand"></i>}
                color="pink"
                subtitle="iOS platform"
              />
              <MetricCard
                title="Android Crashes"
                value={normalized.androidCrashes}
                formatType="number"
                loading={crashLoading}
                icon={<i className="bi bi-robot icon-brand"></i>}
                color="rose"
                subtitle="Android platform"
              />
            </div>
          </div>,

          // Additional charts in show more
          sessionsChart && (
            <div key="sessions-chart" className={compact ? "chart-container-compact" : "chart-container"}>
              <AreaChart
                data={sessionsChart.data}
                comparisonData={sessionsChart.comparisonData}
                label="Total Sessions Over Time"
                formatType="number"
                color="blue"
              />
            </div>
          ),

          platformChartData && (
            <div key="platform-chart" className={compact ? "chart-container-compact" : "chart-container"}>
              <PieChart
                data={platformChartData}
                label="Users by Platform"
                formatType="number"
                showPercentages={true}
                innerRadius={0.5}
              />
            </div>
          )
        ].filter(Boolean)}
      </CollapsibleSection>
    </div>
  );
};

MetricsOverview.propTypes = {
  period: PropTypes.string.isRequired,
  platform: PropTypes.string,
  compact: PropTypes.bool
};

MetricsOverview.defaultProps = {
  platform: null,
  compact: false
};

export default React.memo(MetricsOverview);
